import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { isUserAuthorized, hasPermissionToViewData } from '../../util/userHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

export const ASSET_NAME = 'landing-page';

const RESULT_PAGE_SIZE = 12;

// ================ Action types ================ //

const FETCH_LATEST_LISTINGS_REQUEST = 'app/LandingPage/FETCH_LATEST_LISTINGS_REQUEST';
const FETCH_LATEST_LISTINGS_SUCCESS = 'app/LandingPage/FETCH_LATEST_LISTINGS_SUCCESS';
const FETCH_LATEST_LISTINGS_ERROR = 'app/LandingPage/FETCH_LATEST_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  latestListingIds: [],
  latestListingsInProgress: false,
  latestListingsError: null,
};

const resultIds = data => {
  const listings = data?.data || [];
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);
};

export default function LandingPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LATEST_LISTINGS_REQUEST:
      return {
        ...state,
        latestListingsInProgress: true,
        latestListingsError: null,
      };
    case FETCH_LATEST_LISTINGS_SUCCESS:
      return {
        ...state,
        latestListingIds: resultIds(payload.data),
        latestListingsInProgress: false,
      };
    case FETCH_LATEST_LISTINGS_ERROR:
      return {
        ...state,
        latestListingsInProgress: false,
        latestListingsError: payload,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchLatestListingsRequest = () => ({
  type: FETCH_LATEST_LISTINGS_REQUEST,
});

const fetchLatestListingsSuccess = response => ({
  type: FETCH_LATEST_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

const fetchLatestListingsError = e => ({
  type: FETCH_LATEST_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryLatestListings = config => (dispatch, getState, sdk) => {
  dispatch(fetchLatestListingsRequest());

  const { variantPrefix = 'listing-card' } = config.layout.listingImage;
  // Always request 1:1 square variants for the latest listings carousel
  const aspectWidth = 1;
  const aspectHeight = 1;
  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    sort: 'createdAt',
    perPage: RESULT_PAGE_SIZE,
    // Match SearchPage stock filtering: include stock-tracked listings with qty > 0,
    // plus listings that have no stock tracking at all (services, bookings)
    minStock: 1,
    stockMode: 'match-undefined',
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'price',
      'state',
      'deleted',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.cardStyle',
      'publicData.translations',
      'createdAt',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(fetchLatestListingsSuccess(response));
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(fetchLatestListingsError(error));
      return null;
    });
};

export const loadData = (params, search, config) => (dispatch, getState) => {
  const pageAsset = { landingPage: `content/pages/${ASSET_NAME}.json` };

  const pageAssetPromise = dispatch(fetchPageAssets(pageAsset, true));

  const state = getState();
  const currentUser = state.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
  const isPrivateMarketplace = config?.accessControl?.marketplace?.private === true;
  const canFetchListings =
    !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);

  const latestListingsPromise = canFetchListings
    ? dispatch(queryLatestListings(config))
    : Promise.resolve();

  return Promise.all([pageAssetPromise, latestListingsPromise]);
};
